import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

function uniqueValues(values) {
  return [...new Set((Array.isArray(values) ? values : [])
    .map((item) => String(item ?? "").trim())
    .filter(Boolean))];
}

export default function CreatableDropdownInput({
  id,
  value,
  onChange,
  options = [],
  placeholder,
  loading = false,
  noOptionsText,
  className = "",
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(String(value ?? ""));

  useEffect(() => {
    setQuery(String(value ?? ""));
  }, [value]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        onChange(String(query ?? "").trim());
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [onChange, query]);

  const normalizedOptions = useMemo(() => uniqueValues(options), [options]);

  const filteredOptions = useMemo(() => {
    const q = String(query ?? "").trim().toLowerCase();
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((option) => option.toLowerCase().includes(q));
  }, [normalizedOptions, query]);

  const commitValue = (next) => {
    const trimmed = String(next ?? "").trim();
    setQuery(trimmed);
    onChange(trimmed);
  };

  const showNoResult = !loading && filteredOptions.length === 0;

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          value={query}
          placeholder={placeholder}
          className={`h-11 bg-white border-slate-200 pr-9 ${className}`.trim()}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitValue(query);
              setOpen(false);
            }
            if (event.key === "Escape") {
              setOpen(false);
            }
          }}
          onBlur={() => {
            commitValue(query);
          }}
        />

        <button
          type="button"
          tabIndex={-1}
          aria-label="Toggle options"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((prev) => !prev)}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-slate-400"
        >
          <ChevronDown size={16} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-md">
          <div className="max-h-56 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-2 text-sm text-slate-500">Loading options...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    commitValue(option);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors ${
                    String(value ?? "") === option
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {option}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">
                {showNoResult ? (noOptionsText || "No options available") : ""}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
